import React, { useState } from 'react';
import { X, Truck, Calendar, FileText, AlertTriangle, Clock, Shield, User } from 'lucide-react';
import { getTodayAEST } from '../../utils/dateUtils';

interface VehicleInspection {
  id: string;
  inspection_date: string;
  expiry_date: string;
  inspection_type: string;
  status: string;
}

interface Vehicle {
  id: string;
  registration: string;
  type: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  rego_expiry_date: string | null;
  rego_na?: boolean;
  status: string;
  current_location?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  current_km?: number;
  service_interval_km?: number;
  service_interval_months?: number;
  current_hours?: number;
  service_interval_hours?: number;
  last_service_date?: string;
  last_service_km?: number;
  last_service_hours?: number;
  next_service_due_date?: string;
  next_service_due_km?: number;
  next_service_due_hours?: number;
  nhvr_required?: boolean;
  nhvr_scheduled_date?: string;
  nhvr_town?: string;
  nhvr_inspection?: VehicleInspection;
}

interface VehicleFormProps {
  vehicle?: Vehicle | null;
  onSubmit: (vehicleData: Partial<Vehicle>) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

export const VehicleForm: React.FC<VehicleFormProps> = ({ 
  vehicle, 
  onSubmit, 
  onClose, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    registration: vehicle?.registration || '',
    type: vehicle?.type || 'truck',
    make: vehicle?.make || '',
    model: vehicle?.model || '',
    year: vehicle?.year?.toString() || '',
    vin: vehicle?.vin || '',
    rego_expiry_date: vehicle?.rego_expiry_date || '',
    rego_na: vehicle?.rego_na || false,
    status: vehicle?.status || 'active',
    current_location: vehicle?.current_location || '',
    notes: vehicle?.notes || '',
    nhvr_required: vehicle?.nhvr_required || false,
    nhvr_inspection_date: vehicle?.nhvr_inspection?.inspection_date || '',
    nhvr_next_due_date: vehicle?.nhvr_inspection?.expiry_date || '',
    nhvr_scheduled_date: vehicle?.nhvr_scheduled_date || '',
    nhvr_town: vehicle?.nhvr_town || '',
    current_km: vehicle?.current_km?.toString() || '',
    service_interval_km: vehicle?.service_interval_km?.toString() || '',
    service_interval_months: vehicle?.service_interval_months?.toString() || '',
    current_hours: vehicle?.current_hours?.toString() || '',
    service_interval_hours: vehicle?.service_interval_hours?.toString() || '',
    last_service_date: vehicle?.last_service_date || '',
    last_service_km: vehicle?.last_service_km?.toString() || '',
    last_service_hours: vehicle?.last_service_hours?.toString() || ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { nhvr_required, nhvr_inspection_date, nhvr_next_due_date, rego_na, ...vehicleFields } = formData;

    let next_service_due_date: string | null = null;
    let next_service_due_km: number | null = null;
    let next_service_due_hours: number | null = null;

    if (formData.last_service_date && formData.service_interval_months) {
      const lastDate = new Date(formData.last_service_date);
      const nextDate = new Date(lastDate);
      nextDate.setMonth(lastDate.getMonth() + Number(formData.service_interval_months));
      next_service_due_date = nextDate.toISOString().split('T')[0];
    }

    if (formData.last_service_km && formData.service_interval_km) {
      next_service_due_km = Number(formData.last_service_km) + Number(formData.service_interval_km);
    }

    if (formData.last_service_hours && formData.service_interval_hours) {
      next_service_due_hours = Number(formData.last_service_hours) + Number(formData.service_interval_hours);
    }

    const vehicleData = {
      ...vehicleFields,
      year: formData.year ? Number(formData.year) : null,
      registration: formData.registration.toUpperCase(),
      vin: formData.vin.trim() === '' ? null : formData.vin.trim(),
      rego_na: formData.rego_na,
      rego_expiry_date: formData.rego_na ? null : vehicleFields.rego_expiry_date,
      nhvr_required: formData.nhvr_required,
      nhvr_scheduled_date: formData.nhvr_required ? (formData.nhvr_scheduled_date || null) : null,
      nhvr_town: formData.nhvr_required ? (formData.nhvr_town || null) : null,
      current_km: formData.current_km ? Number(formData.current_km) : null,
      service_interval_km: formData.service_interval_km ? Number(formData.service_interval_km) : null,
      service_interval_months: formData.service_interval_months ? Number(formData.service_interval_months) : null,
      current_hours: formData.current_hours ? Number(formData.current_hours) : null,
      service_interval_hours: formData.service_interval_hours ? Number(formData.service_interval_hours) : null,
      last_service_date: formData.last_service_date || null,
      last_service_km: formData.last_service_km ? Number(formData.last_service_km) : null,
      last_service_hours: formData.last_service_hours ? Number(formData.last_service_hours) : null,
      next_service_due_date,
      next_service_due_km,
      next_service_due_hours
    };

    const originalInspectionDate = vehicle?.nhvr_inspection?.inspection_date || '';
    let nhvrInspectionData = null;
    if (formData.nhvr_required && formData.nhvr_inspection_date && formData.nhvr_inspection_date !== originalInspectionDate) {
      nhvrInspectionData = {
        inspection_date: formData.nhvr_inspection_date
      };
    }

    await onSubmit(vehicleData, nhvrInspectionData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Auto-calculate next due date when inspection date changes
  const handleInspectionDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inspectionDate = e.target.value;
    setFormData(prev => ({ ...prev, nhvr_inspection_date: inspectionDate }));
    
    if (inspectionDate) {
      const date = new Date(inspectionDate);
      const nextDueDate = new Date(date);
      nextDueDate.setFullYear(date.getFullYear() + 1); // Add 12 months
      
      setFormData(prev => ({ 
        ...prev, 
        nhvr_next_due_date: nextDueDate.toISOString().split('T')[0] 
      }));
    } else {
      setFormData(prev => ({ ...prev, nhvr_next_due_date: '' }));
    }
  };

  // Check if registration is expiring soon
  const isRegoExpiringSoon = () => {
    if (formData.rego_na || !formData.rego_expiry_date) return false;
    const today = new Date(getTodayAEST());
    const expiryDate = new Date(formData.rego_expiry_date);
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const isRegoOverdue = () => {
    if (formData.rego_na || !formData.rego_expiry_date) return false;
    const today = new Date(getTodayAEST());
    const expiryDate = new Date(formData.rego_expiry_date);
    return expiryDate < today;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Truck className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              {vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Registration Number *
              </label>
              <input
                type="text"
                name="registration"
                required
                value={formData.registration}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono uppercase"
                placeholder="ABC123"
                style={{ textTransform: 'uppercase' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle Type *
              </label>
              <select
                name="type"
                required
                value={formData.type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="truck">Truck</option>
                <option value="trailer">Trailer</option>
                <option value="ute">Ute</option>
                <option value="van">Van</option>
                <option value="car">Car</option>
                <option value="excavator">Excavator</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Make
              </label>
              <input
                type="text"
                name="make"
                value={formData.make}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Toyota, Ford, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model
              </label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Hilux, Ranger, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <input
                type="number"
                name="year"
                min="1900"
                max={new Date().getFullYear() + 1}
                value={formData.year}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="2020"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              VIN (Vehicle Identification Number)
            </label>
            <input
              type="text"
              name="vin"
              value={formData.vin}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono"
              placeholder="1HGBH41JXMN109186"
              maxLength={17}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Registration Expiry Date {!formData.rego_na && '*'}
                </label>
                <label className="flex items-center space-x-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.rego_na}
                    onChange={(e) => setFormData(prev => ({ ...prev, rego_na: e.target.checked, rego_expiry_date: e.target.checked ? '' : prev.rego_expiry_date }))}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 h-4 w-4"
                  />
                  <span className="text-sm font-medium text-gray-600">N/A</span>
                </label>
              </div>
              {formData.rego_na ? (
                <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-500">
                  Registration not applicable — vehicle off road
                </div>
              ) : (
                <input
                  type="date"
                  name="rego_expiry_date"
                  required
                  value={formData.rego_expiry_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              )}
              {!formData.rego_na && isRegoOverdue() && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center space-x-1">
                    <AlertTriangle className="h-3 w-3 text-red-600" />
                    <span className="text-xs font-medium text-red-800">Registration is overdue!</span>
                  </div>
                </div>
              )}
              {!formData.rego_na && isRegoExpiringSoon() && !isRegoOverdue() && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-center space-x-1">
                    <Clock className="h-3 w-3 text-yellow-600" />
                    <span className="text-xs font-medium text-yellow-800">Registration expires soon</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="maintenance">Maintenance</option>
                <option value="sold">Sold</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Location
            </label>
            <input
              type="text"
              name="current_location"
              value={formData.current_location}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="e.g., Site A, Workshop, Yard"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Notes
            </label>
            <textarea
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Additional notes about the vehicle..."
            />
          </div>

          {/* Service Scheduling Section */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h4 className="text-lg font-medium text-gray-900 flex items-center mb-4">
              <Clock className="h-5 w-5 mr-2 text-green-600" />
              Service Scheduling
            </h4>

            <p className="text-sm text-green-800 mb-4">
              Set service intervals to track when this vehicle needs servicing. Service can be scheduled by kilometers, hours (engine hours), time (months), or any combination.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Kilometers
                </label>
                <input
                  type="number"
                  name="current_km"
                  min="0"
                  step="0.01"
                  value={formData.current_km}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., 60000"
                />
                <p className="text-xs text-gray-500 mt-1">Current odometer reading</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Interval (km)
                </label>
                <input
                  type="number"
                  name="service_interval_km"
                  min="0"
                  value={formData.service_interval_km}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., 10000"
                />
                <p className="text-xs text-gray-500 mt-1">Service every X kilometers</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Hours
                </label>
                <input
                  type="number"
                  name="current_hours"
                  min="0"
                  step="0.1"
                  value={formData.current_hours}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., 1500"
                />
                <p className="text-xs text-gray-500 mt-1">Current engine hours</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Interval (hours)
                </label>
                <input
                  type="number"
                  name="service_interval_hours"
                  min="0"
                  value={formData.service_interval_hours}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., 250"
                />
                <p className="text-xs text-gray-500 mt-1">Service every X hours</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Interval (months)
              </label>
              <input
                type="number"
                name="service_interval_months"
                min="0"
                value={formData.service_interval_months}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="e.g., 6"
              />
              <p className="text-xs text-gray-500 mt-1">Service every X months</p>
            </div>

            {/* Last Service Baseline */}
            <div className="border-t border-green-200 pt-4 mt-4">
              <h5 className="font-medium text-gray-800 mb-3">Last Service Details</h5>
              <p className="text-sm text-green-800 mb-3">
                Enter when the last service was performed so the system can calculate when the next one is due.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Service Date
                  </label>
                  <input
                    type="date"
                    name="last_service_date"
                    value={formData.last_service_date}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {formData.last_service_date && formData.service_interval_months && (
                    <p className="text-xs text-green-700 mt-1">
                      Next due: {(() => {
                        const d = new Date(formData.last_service_date);
                        d.setMonth(d.getMonth() + Number(formData.service_interval_months));
                        return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
                      })()}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    KM at Last Service
                  </label>
                  <input
                    type="number"
                    name="last_service_km"
                    min="0"
                    step="0.01"
                    value={formData.last_service_km}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., 50000"
                  />
                  {formData.last_service_km && formData.service_interval_km && (
                    <p className="text-xs text-green-700 mt-1">
                      Next due at: {Math.round(Number(formData.last_service_km) + Number(formData.service_interval_km)).toLocaleString()} km
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hours at Last Service
                  </label>
                  <input
                    type="number"
                    name="last_service_hours"
                    min="0"
                    step="0.1"
                    value={formData.last_service_hours}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., 1200"
                  />
                  {formData.last_service_hours && formData.service_interval_hours && (
                    <p className="text-xs text-green-700 mt-1">
                      Next due at: {Number(formData.last_service_hours) + Number(formData.service_interval_hours)} h
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-green-100 border border-green-300 rounded-lg p-3 mt-4">
              <p className="text-xs text-green-800">
                <strong>Examples:</strong>
              </p>
              <ul className="text-xs text-green-800 mt-2 space-y-1 list-disc list-inside">
                <li>Service at 60,000km OR 6 months (whichever comes first)</li>
                <li>Service at 500 engine hours OR 6 months (whichever comes first)</li>
                <li>Service at 60,000km OR 500 hours OR 6 months (any condition triggers service)</li>
              </ul>
            </div>
          </div>

          {/* NHVR Inspection Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-blue-600" />
                NHVR Inspection
              </h4>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Required</label>
                <input
                  type="checkbox"
                  checked={formData.nhvr_required}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    nhvr_required: e.target.checked,
                    nhvr_inspection_date: e.target.checked ? prev.nhvr_inspection_date : '',
                    nhvr_next_due_date: e.target.checked ? prev.nhvr_next_due_date : ''
                  }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                />
              </div>
            </div>
            
            <p className="text-sm text-blue-800 mb-4">
              Check this box if the vehicle requires NHVR (National Heavy Vehicle Regulator) inspections. 
              Typically required for commercial vehicles over 4.5 tonnes GVM.
            </p>

            {formData.nhvr_required && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      Last Inspection Date
                    </label>
                    <input
                      type="date"
                      value={formData.nhvr_inspection_date}
                      onChange={handleInspectionDateChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      Next Due Date
                    </label>
                    <input
                      type="date"
                      value={formData.nhvr_next_due_date}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                      placeholder="Auto-calculated"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated (12 months from inspection date)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      Scheduled Inspection Date
                    </label>
                    <input
                      type="date"
                      name="nhvr_scheduled_date"
                      value={formData.nhvr_scheduled_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, nhvr_scheduled_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">When is the next inspection scheduled?</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Inspection Town/Location
                  </label>
                  <input
                    type="text"
                    name="nhvr_town"
                    value={formData.nhvr_town}
                    onChange={(e) => setFormData(prev => ({ ...prev, nhvr_town: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Sydney, Melbourne, Brisbane..."
                  />
                </div>

                <div className="bg-blue-100 border border-blue-300 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Enter the last inspection date and the system will automatically calculate when the next inspection is due (12 months later). 
                    Leave date empty if no inspection has been completed yet.
                  </p>
                </div>
              </div>
            )}

            {!formData.nhvr_required && (
              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">
                  This vehicle does not require NHVR inspections
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Light vehicles under 4.5 tonnes GVM typically don't require NHVR inspections
                </p>
              </div>
            )}
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : (vehicle ? 'Update Vehicle' : 'Add Vehicle')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};